import { useEffect, useState } from 'react';

const STORAGE_KEY = 'v91:favorites';

const readFavorites = (): string[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

const writeFavorites = (ids: string[]) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // storage unavailable — favorites stay in memory only
  }
};

const listeners = new Set<(ids: string[]) => void>();
let snapshot: string[] = typeof window === 'undefined' ? [] : readFavorites();

const emit = (ids: string[]) => {
  snapshot = ids;
  listeners.forEach((listener) => listener(ids));
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) emit(readFavorites());
  });
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(snapshot);

  useEffect(() => {
    listeners.add(setFavorites);
    return () => {
      listeners.delete(setFavorites);
    };
  }, []);

  const toggleFavorite = (id: string) => {
    const next = snapshot.includes(id) ? snapshot.filter((item) => item !== id) : [id, ...snapshot];
    writeFavorites(next);
    emit(next);
  };

  const isFavorite = (id: string) => favorites.includes(id);

  return { favorites, toggleFavorite, isFavorite };
}

export function useFavoriteCount() {
  const [favorites, setFavorites] = useState<string[]>(snapshot);

  useEffect(() => {
    listeners.add(setFavorites);
    return () => {
      listeners.delete(setFavorites);
    };
  }, []);

  return favorites.length;
}